import {noop} from '@benchling/js-utils';
import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as OnboardingActions from 'actions/OnboardingActions';
import {DigestAnalysis} from 'analyses/Analyses';
import Digests from 'analyses/Digests';
import DigestTabState from 'analyses/DigestTabState';
import * as analytics from 'analytics/Analytics';
import * as biojs from 'BioJS';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import {Selection, Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import * as SequenceAnalysisConstants from 'constants/SequenceAnalysisConstants';
import {RestrictionPanelState} from 'FileStates';
import CutSite from 'molbio/enzymes/backbone/CutSite';
import Enzymes, {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import EnzymeConstants, {Cut, EnzymePreferences} from 'molbio/enzymes/EnzymeConstants';
import EnzymeProviderSelector from 'molbio/enzymes/EnzymeProviderSelector';
import * as EnzymeUtil from 'molbio/enzymes/EnzymeUtil';
import * as sequtil from 'molbio/SeqUtil';
import ToolbarSignupMessage from 'nav/share/growth/ToolbarSignupMessage';
import {showValueMessagingForSharedFile} from 'nav/share/growth/Util';
import * as navigation from 'Navigation';
import {BufferStates, TabState} from 'Navigation';
import notifications from 'notifications';
import * as popup from 'Popup';
import preferences from 'Preferences';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import ActiveEnzymeItem from 'toolbar/restriction-panel/ActiveEnzymeItem';
import DeleteDigestConfirmationModal from 'toolbar/restriction-panel/DeleteDigestConfirmationModal';
import DigestLibrary from 'toolbar/restriction-panel/DigestLibrary';
import EnzymeCatalogItem from 'toolbar/restriction-panel/EnzymeCatalogItem';
import EnzymePreviewTemplate from 'toolbar/restriction-panel/EnzymePreviewTemplate';
// @ts-expect-error
import logUndefinedState from 'toolbar/shared/logUndefinedState';
import {renderPanelTabs} from 'toolbar/shared/PanelUtil';
import {SEQUENCE_DIGEST_PANEL_DIGESTS, SEQUENCE_DIGEST_PANEL_NEW_DIGEST} from 'toolbar/ToolbarConstants';
import runModal from 'ui-library/runModal';
import CallQueuer from 'util/CallQueuer';
import throttle, {ThrottledFunction} from 'util/throttle';

export default class RestrictionPanelView extends Backbone.View<RestrictionPanelState> {
  buffers!: BufferStates;
  callQueuer!: CallQueuer;
  enzymes!: Enzymes;
  panelState!: RestrictionPanelState;
  selection!: Selection;
  sequence!: Sequence;
  sidebarEnzyme!: Enzyme | null;
  trackSearch!: ThrottledFunction<(query: string) => void>;

  static initClass(): void {
    this.prototype.el = '#restriction-panel';

    this.prototype.events = {
      // @ts-expect-error
      'click .new-digest-title': 'showNewDigest',
      'keyup .search': 'filter',
      'change .search': 'filter',
      'change .enzyme-lists': 'changeViewEnzymes',
      'click .my-digests-title': 'showMyDigests',
      'change .view-enzymes': 'changeViewEnzymes',
      'click .run-digest': 'runDigest',
      'click .cut-position': 'jumpToPosition',
      'click .close': 'closeSidebar',
      'click .enzyme-link': 'trackEnzymeLinkClick',
      'click .checkbox label': 'changeCompatible',
      'change .cut-region select': 'changeCutRegion',
      'click a[data-enzymeId]': 'clickEnzymeHandler',
      'click .js-clear-selected': function (e: JQueryEventObject) {
        e.preventDefault();
        // @ts-expect-error
        this.clearEnzymes();
      },
    };

    this.prototype.trackSearch = throttle((query) => {
      if (query.length > 1) {
        analytics.track('digest', 'search_enzymes', {query});
      }
    }, 5000);
  }

  initialize(): void {
    this.providerChangeHandler = this.providerChangeHandler.bind(this);
    this.$el.find('.my-digests-subpanel').hide();
    this.callQueuer = new CallQueuer(1);
  }

  changeCutRegion(e: {target: {value: string}}): void {
    if (this.panelState == null) {
      logUndefinedState('RestrictionPanelView', 'panelState', this, e);
      return;
    }
    this.panelState.set({cutRegion: e.target.value});
  }

  trackEnzymeLinkClick(e: JQueryEventObject): void {
    const url = $(e.currentTarget).attr('href');
    analytics.track('digest', 'clicked_enzyme_link', {url});
  }

  getOtherSubPanel(
    panel: typeof SEQUENCE_DIGEST_PANEL_NEW_DIGEST | typeof SEQUENCE_DIGEST_PANEL_DIGESTS
  ): typeof SEQUENCE_DIGEST_PANEL_NEW_DIGEST | typeof SEQUENCE_DIGEST_PANEL_DIGESTS {
    if (panel === SEQUENCE_DIGEST_PANEL_NEW_DIGEST) {
      return SEQUENCE_DIGEST_PANEL_DIGESTS;
    } else {
      return SEQUENCE_DIGEST_PANEL_NEW_DIGEST;
    }
  }

  findOpenTab(model: DigestAnalysis): TabState | null {
    for (const buffer of this.buffers.models) {
      for (const tab of buffer.tabs().models) {
        if (tab.get('analysis') === model) {
          return tab;
        }
      }
    }
    return null;
  }

  showSubPanel(): void {
    const panel = this.panelState.get('activeSubPanel');
    const other = this.getOtherSubPanel(panel);
    this.$el.find(`.${panel}-title`).parent().addClass('active');
    this.$el.find(`.${other}-title`).parent().removeClass('active');
    renderPanelTabs(
      this.panelState,
      [
        [SEQUENCE_DIGEST_PANEL_NEW_DIGEST, 'New Digest'],
        [SEQUENCE_DIGEST_PANEL_DIGESTS, 'Saved Digests'],
      ],
      this.$('.panel-title')[0]
    );
    this.$el.find(`.${panel}-subpanel`).show();
    this.$el.find('.panel-content').scrollTop(0);
    this.$el.find(`.${other}-subpanel`).hide();
    this.panelState.trigger('resize');
  }

  showNewDigest(): void {
    this.panelState.set('activeSubPanel', SEQUENCE_DIGEST_PANEL_NEW_DIGEST);
  }

  showMyDigests(): void {
    this.panelState.set('activeSubPanel', SEQUENCE_DIGEST_PANEL_DIGESTS);
  }

  clickEnzymeHandler(e: JQueryEventObject): void {
    const enzymeId = $(e.target).attr('data-enzymeId');
    const enzyme = this.enzymes.get(enzymeId);
    enzyme.trigger('zoomTo', {offset: 0, enzyme});
    enzyme.trigger('showDetail', enzyme);
  }

  setupEnzymes(doChange: boolean): void {
    let enzymesProvider;
    let providerList;
    const viewEnzymes = this.$el.find('.view-enzymes').val();
    this.panelState.set({viewEnzymes});
    preferences.set({[EnzymePreferences.VIEW_ENZYMES]: viewEnzymes});
    const $select = this.$el.find('.enzyme-lists');
    const selectedEnzymeListId = JSON.parse($select.val());

    if (preferences.get(EnzymePreferences.ENZYME_LIST_ID) === selectedEnzymeListId) {
      // If the enzymeListId hasn't changed, keep the provider the same.
      enzymesProvider = preferences.get(EnzymePreferences.PROVIDER);
      // eslint-disable-next-line no-cond-assign
    } else if ((providerList = EnzymeUtil.getProviderList(selectedEnzymeListId)) != null) {
      enzymesProvider = providerList.defaultProvider.name;
    } else {
      enzymesProvider = EnzymeConstants.baseProviders[0].name;
    }

    this.panelState.set({enzymeListId: selectedEnzymeListId, enzymesProvider});
    preferences.set({
      [EnzymePreferences.ENZYME_LIST_ID]: selectedEnzymeListId,
      [EnzymePreferences.PROVIDER]: enzymesProvider,
    });

    if (doChange) {
      this.enzymes.each((enzyme) => {
        this.panelState.changeViewEnzyme(enzyme, null, {trigger: false});
      });
      this.sequence.get('cutsites').trigger('cutsitesChanged');
    }

    this.filter();
  }

  changeViewEnzymes(e: JQueryEventObject): void {
    if (this.panelState == null) {
      logUndefinedState('RestrictionPanelView', 'panelState', this, e);
      return;
    }
    this.setupEnzymes(true);
  }

  renderEnzymeCatalog(): void {
    renderToDOM(
      <>
        {this.enzymes.models.map((enzyme) => (
          <EnzymeCatalogItem
            active={enzyme.get('active')}
            catalogVisible={enzyme.get('catalogVisible')}
            hideCutPositions={this.sequence.isReallyBig()}
            highlight={enzyme.highlight()}
            key={enzyme.id}
            name={enzyme.get('name')}
            onClick={() => {
              analytics.track('digest', 'select_enzyme', {enzyme: enzyme.get('name')});
              const newState = !enzyme.get('active');
              enzyme.set('active', newState);
            }}
            onMouseOver={() => {
              enzyme.trigger('showDetail', enzyme);
            }}
            sites={enzyme.get('cutPositions').length}
          />
        ))}
      </>,
      this.$el.find('table.catalogue tbody')[0]
    );
  }

  renderActiveEnzymes(): void {
    renderToDOM(
      <>
        {/* selectedEnzymes models are the same models as catalog enzyme models, so all these triggers and
        will cause the catalog enzymes to rerender as well */}
        {this.panelState.get('selectedEnzymes').map((enzyme: Enzyme) => (
          <ActiveEnzymeItem
            color={enzyme.color()!}
            key={enzyme.id}
            onClick={() => {
              enzyme.set({active: false, highlight: false});
            }}
            onMouseOver={(offset: number) => {
              enzyme.trigger('zoomTo', {offset, enzyme});
              enzyme.trigger('showDetail', enzyme);
              enzyme.set('highlight', true);
            }}
            onMouseOut={() => {
              enzyme.set('highlight', false);
            }}
            name={enzyme.name()}
          />
        ))}
      </>,
      this.$el.find('table.active-enzymes tbody')[0]
    );
  }

  renderEnzymes(): void {
    this.renderEnzymeCatalog();
    this.renderActiveEnzymes();
    this.setupEnzymes(false);
  }

  closeSidebar(): void {
    this.$el.find('.panel').removeClass('show-sidebar');
    this.callQueuer.purge();
    this.sidebarEnzyme = null;
  }

  // TODO is there a named type
  providerChangeHandler(provider: {name: string}): void {
    this.panelState.set('enzymesProvider', provider.name);
    preferences.set(EnzymePreferences.PROVIDER, provider.name);
  }

  renderSidebar(enzyme: Enzyme | null = this.sidebarEnzyme): void {
    let i;
    if (enzyme == null) {
      return;
    }
    this.sidebarEnzyme = enzyme;

    const hideJump = this.sequence.isReallyBig();

    // Choose a provider, ideally the one they have selected as a preference.
    const desiredProviderName = this.panelState.get('enzymesProvider');
    const selectedProvider = EnzymeUtil.getPreferredProviderForEnzyme([enzyme.id], desiredProviderName);
    const selectedBaseProvider = EnzymeUtil.getBaseProvider(selectedProvider.name);
    const providedEnzymes = enzyme.get('providedEnzymes');
    const providedEnzyme = providedEnzymes[selectedProvider.name];

    // For the selected provider, find all specified activities for its buffers, and wrap them into
    // a reasonable number of columns for display (4 by default, 3 if there are a multiple of 3 buffers).
    // Filter on selectedProvider.buffers to ensure that the buffers are ordered consistently.
    // eslint-disable-next-line benchlint/no-in-operator
    const buffers = selectedProvider.buffers.filter((buffer) => buffer in providedEnzyme.activities);
    const optimalBuffers = EnzymeUtil.calculateOptimalBuffers([enzyme.id], selectedProvider.name);
    const activities = buffers.map((buffer) => ({
      buffer: EnzymeUtil.formatBufferName(buffer),
      value: providedEnzyme.activities[buffer],
      highlighted: optimalBuffers.includes(buffer),
    }));
    const width = activities.length % 3 === 0 && activities.length % 4 !== 0 ? 3 : 4;
    const activityRows = [];
    for (i = 0; i < activities.length / width; i++) {
      activityRows.push(activities.slice(width * i, width * (i + 1)));
    }

    const t = $(
      // eslint-disable-next-line new-cap
      EnzymePreviewTemplate({
        hideJump,
        // eslint-disable-next-line no-restricted-syntax
        enzyme: _.extend(enzyme.toJSON(), {
          isoschizomers: this.enzymes.getEnzymesJSON(enzyme.get('isoschizomers')),
        }),
        providedEnzyme: {
          activityRows,
          url: providedEnzyme.url,
          baseName: selectedBaseProvider.name,
          inactivationTemperature: providedEnzyme.inactivationTemperature!,
          incubationTemperature: providedEnzyme.incubationTemperature,
        },
      })
    );

    const enzymeProviderSelector = (
      <EnzymeProviderSelector
        providers={Object.keys(providedEnzymes).map((name) => EnzymeUtil.getProvider(name))}
        value={selectedProvider}
        /* eslint-disable-next-line @typescript-eslint/unbound-method */
        onChange={this.providerChangeHandler}
      />
    );
    renderToDOM(enzymeProviderSelector, t.find('.js-enzyme-provider')[0]);

    const cpTable = t.find('.cut-positions table');

    if (!hideJump) {
      const startIndex = this.sequence.startIndex();
      this.callQueuer.purge();
      _.chain(enzyme.get('cutPositions'))
        .groupBy((cut, i) => Math.floor(i / 100))
        .values()
        .each(function (cuts: Array<Cut>) {
          // @ts-expect-error shadows outer this
          return this.callQueuer.add(null, () =>
            cpTable.append(
              // eslint-disable-next-line no-restricted-syntax
              _.map(cuts, (cut) => {
                const pos = sequtil.modelToDisplay(cut.position, startIndex);
                return `<tr><td class='cut-position'>${pos}</td></tr>`;
              })
            )
          );
        }, this);
      this.callQueuer.resume();
    }

    const cutInfo = t.find('.cut-info');

    const cut = enzyme.get('cut');
    const seq = enzyme.get('seq');
    const bottomCut = cut + enzyme.get('offset');
    let diff = cut - seq.length;
    const bottomDiff = diff + enzyme.get('offset');
    let offset = 0;

    const topText = $('<div>')
      // @ts-expect-error you can't pass an array to jQuery.html
      // eslint-disable-next-line no-restricted-syntax
      .html(_.map(seq.split(''), (char) => $(`<span>${char}</span>`)))
      .addClass('enzyme-top')
      .attr('data-test-component', 'PanelSidebar')
      .attr('data-test-element', 'recognitionSiteForward');
    const bottomText = $('<div>')
      .html(
        // @ts-expect-error you can't pass an array to jQuery.html
        // eslint-disable-next-line no-restricted-syntax
        _.map(biojs.util.complement(seq, DNA_COMPLEMENT_TABLE).split(''), (char) => $(`<span>${char}</span>`))
      )
      .addClass('enzyme-bottom')
      .attr('data-test-component', 'PanelSidebar')
      .attr('data-test-element', 'recognitionSiteReverse');
    cutInfo.append(topText).append(bottomText);

    const cutSymbol = $('<span>').addClass('cut-symbol');
    const render = (
      text: JQuery,
      cut: number | null,
      diff: number,
      offsetDir?: StrandOfDoubleStranded
    ): JQuery | number => {
      if (diff === 1) {
        text.append('<span>N</span>');
        return text.append(cutSymbol.clone());
      } else if (diff > 1) {
        text.append(`<span>(N)<sub>${diff}</sub></span>`);
        return text.append(cutSymbol.clone());
      } else if (cut === -1) {
        text.prepend('<span>N</span>');
        text.prepend(cutSymbol.clone());
        // eslint-disable-next-line no-return-assign
        return (offset += Number(offsetDir));
        // @ts-expect-error We never hit this branch if cut is null
      } else if (cut < -1) {
        // @ts-expect-error We never hit this branch if cut is null
        text.prepend(`<span><sub>${-cut}</sub>(N)</span>`);
        text.prepend(cutSymbol.clone());
        // @ts-expect-error We never hit this branch if cut or offsetDir is null
        // eslint-disable-next-line no-return-assign
        return (offset += offsetDir * `${-cut}(N)`.length);
      } else if (cut === seq.length) {
        return text.append(cutSymbol.clone());
      } else {
        // @ts-expect-error We never hit this branch if cut is null
        return text.find('span').eq(cut).before(cutSymbol.clone());
      }
    };

    render(topText, cut, diff, 1);
    render(bottomText, bottomCut, bottomDiff, -1);

    if (enzyme.doubleCutter()) {
      diff = enzyme.get('cut2') - seq.length;
      render(topText, null, diff, 1);
      render(bottomText, null, diff + enzyme.get('offset2'));
    }

    if (offset > 0) {
      cutInfo.addClass('text-right');
    } else if (offset <= 0) {
      cutInfo.addClass('text-left');
    }
    // @ts-expect-error You're not supposed to pass JQuery to .html, you have to do a string, or you should
    // use .append
    this.$el.find('.new-digest-subpanel .panel-sidebar').html(t);
    this.$el.find('.panel').addClass('show-sidebar');
  }

  jumpToPosition(e: JQueryEventObject): void {
    const position = sequtil.displayToModel(
      parseInt($(e.currentTarget).html(), 10),
      this.sequence.startIndex()
    );
    const enzyme = this.sidebarEnzyme;
    const existing = this.sequence
      .get('cutsites')
      .filter((cutsite) => cutsite.get('position') === position && cutsite.get('enzyme') === enzyme);

    // if the cutsite isn't visible, lets make a temporary one to show for one second
    if (existing.length === 0) {
      const cutsite = new CutSite({enzyme, position, fontColor: 'blue'});
      this.sequence.get('cutsites').add(cutsite);
      // eslint-disable-next-line no-restricted-syntax
      const f = _.bind(function () {
        // @ts-expect-error shadows outer this
        this.sequence.get('cutsites').remove(cutsite);
      }, this);
      _.delay(f, 1000);
    }

    this.sequence.get('selection').reset(position);
    this.sequence.trigger('cursorJump', position);
  }

  runDigest(): void {
    const selectedEnzymes = this.panelState.get('selectedEnzymes');
    if (selectedEnzymes.length === 0) {
      notifications.error('You must select at least one enzyme.');
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    const doDigest = _.bind(function () {
      OnboardingActions.completeOnboardingTask(
        OnboardingConstants.MODULES.SEQUENCE,
        OnboardingConstants.RUN_DIGEST
      );
      const enzymeIds = selectedEnzymes.models.map((enzyme: Enzyme) => enzyme.id);
      const analysis = new DigestAnalysis({
        analysis_type: SequenceAnalysisConstants.DIGEST,
        enzymes: enzymeIds,
        name: 'Digest',
      });
      // @ts-expect-error shadows outer this
      const tab = new DigestTabState({analysis, sequence: this.sequence});

      // @ts-expect-error shadows outer this
      const buffers = this.panelState.get('buffers');
      if (buffers.length === 0) {
        buffers.add(new navigation.BufferState());
      }
      buffers.addTab(tab, {side: 'right'});
      Digests.get().add(tab);
      tab.set({active: true});
      // @ts-expect-error shadows outer this
      this.clearEnzymes();
      notifications.success('Digest successfully created.');
      // @ts-expect-error shadows outer this
      this.panelState.trigger('close');
    }, this);

    // eslint-disable-next-line no-restricted-syntax
    const es = _.map(selectedEnzymes.models, (e: Enzyme) => e.toJSON());
    const seq = this.sequence.get('_sequence');
    const overlaps = seq.enzymeOverlap(es);

    if (overlaps.length > 0) {
      let msg = `<p>The resulting digest may be inaccurate, as the following enzymes have overlapping cutsites:</p>
<div class='scroll'>
<table class="table table-condensed">
  <thead>
    <tr><th>Start</th><th>End</th><th>Enzymes</th></tr>
  </thead>
  <tbody>\
`;
      for (const overlap of overlaps) {
        const enzymeList = _.chain(overlap.enzymes)
          .countBy((e) => e)
          .pairs()
          // @ts-expect-error After .countBy and .pairs this should be fine; will revise when reactifying
          .map((x) => x[0] + (x[1] > 1 ? '*' : ''))
          .value()
          .join(', ');

        msg += `    <tr>
  <td>${overlap.start}</td>
  <td>${overlap.end}</td>
  <td>${enzymeList}</td>
</tr>\
`;
      }
      msg += `  </tbody>
</table>
</div>
<p class='table-note'>* The enzyme has multiple cutsites in the same region.</p>
<p>Proceed with digest anyway?</p>\
`;
      new popup.Confirm({title: 'Conflicting Enzymes', msg})
        .choice('OK', 'btn-af-primary', doDigest)
        .choice('Cancel', 'btn-af-default')
        .show();
    } else {
      doDigest();
    }

    analytics.track('digest', 'ran_digest', {enzyme_count: selectedEnzymes.length});
  }

  clearEnzymes(): void {
    this.panelState
      .get('selectedEnzymes')
      // Make a copy of the enzymes since changing their active status will remove them from selectedEnzymes
      .models.slice()
      .map((enzyme: Enzyme) => enzyme.set({active: false, color: null}));
  }

  async deleteDigest(digest: DigestAnalysis): Promise<void> {
    try {
      await digest.destroy();
      notifications.success(
        <>
          Digest <strong>{digest.get('name')}</strong> successfully deleted.
        </>
      );
      analytics.track('digest', 'deleted_digest');
      this.findOpenTab(digest)?.close();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.error('An error occurred while trying to delete the digest.');
    }
  }

  renderLibrary(): void {
    if (!this.sequence.isLongDNASequence()) {
      // Digests are only applicable to long DNA sequences. Theoretically, other kinds of sequences shouldn't
      // have any digests in the first place, but in practice, there are some non-long DNA sequences that
      // have digests due to past bugs.
      return;
    }
    renderToDOM(
      <DigestLibrary
        digests={
          this.sequence
            .get('analyses')
            .filter(
              (analysis) => analysis.get('analysis_type') === SequenceAnalysisConstants.DIGEST
            ) as Array<DigestAnalysis>
        }
        disableDelete={!this.sequence.allowFeatureEdits({ignoreVersion: true})}
        enzymes={this.sequence.get('enzymes')}
        onClickItem={async (digest) => {
          analytics.track('digest', 'opened_digest');
          const existingTab = this.findOpenTab(digest);
          if (existingTab != null) {
            existingTab.set({active: true});
          } else {
            await digest.fetch();
            const tab = new DigestTabState({analysis: digest, sequence: this.sequence});
            this.panelState.get('buffers').addTab(tab);
            Digests.get().add(tab);
            this.panelState.trigger('close');
          }
        }}
        onDeleteItem={(digest) => {
          const existingTab = this.findOpenTab(digest);
          if (existingTab == null) {
            this.deleteDigest(digest);
          } else {
            runModal(DeleteDigestConfirmationModal, {
              digest,
              onRequestClose: noop,
              onRequestSubmit: () => {
                this.deleteDigest(digest);
              },
            });
          }
        }}
      />,
      this.$el.find('table.digest-library tbody')[0]
    );
  }

  filter(): void {
    this.panelState.set('search', this.$el.find('.search').val());
    const searchText = this.panelState.get('search').toLowerCase();
    this.trackSearch(searchText);
    this.enzymes.batchModify(() => {
      this.enzymes.each((enzyme) => {
        this.filterEnzyme(enzyme);
      });
    });
  }

  filterEnzyme(enzyme: Enzyme): void {
    const searchText = this.panelState.get('search').toLowerCase();
    const parts = searchText.split(' ');
    const ignoreCutPositionCount = this.sequence.isReallyBig();

    if (EnzymeUtil.doesEnzymeListContain(this.panelState.get('enzymeListId'), enzyme.id)) {
      const cutRegion = this.panelState.get('cutRegion');
      let inCutRegion;
      switch (cutRegion) {
        case 'anywhere':
          inCutRegion = true;
          break;
        default: {
          const inSelections = enzyme.get('cutPositions').map((c: Cut) => {
            if (this.selection.length() === 0) {
              return false;
            }

            // Don't use isRangeOverlap when start/end are the same, just use baseInRange.
            if (enzyme.get('offset') === 0) {
              return sequtil.baseInRange(c.position, this.selection.start(), this.selection.end()!);
            }

            let start = c.position;
            let end = sequtil.mod(start + enzyme.get('offset'), this.sequence.length());
            // Reverse start and end for negative offset.
            if (enzyme.get('offset') < 0) {
              [start, end] = [end, start];
            }
            return sequtil.isRangeOverlap(start, end, this.selection.start(), this.selection.end()!);
          });

          // eslint-disable-next-line default-case
          switch (cutRegion) {
            case 'inselection':
              inCutRegion = _.any(inSelections);
              break;
            case 'exceptselection':
              inCutRegion = !_.any(inSelections);
              break;
            case 'onlyselection':
              inCutRegion = _.all(inSelections) && inSelections.length !== 0;
          }
        }
      }
      if (inCutRegion) {
        // If the enzyme of any of its isoschizomers match the search text, consider it a match
        // for the enzyme.
        const names = [enzyme.id, ...enzyme.get('isoschizomers')].map((enzymeId) =>
          this.enzymes.get(enzymeId).get('name').toLowerCase()
        );

        if (!Number.isNaN(parseInt(searchText, 10)) && !ignoreCutPositionCount) {
          enzyme.set('catalogVisible', enzyme.get('cutPositions').length === parseInt(searchText, 10));
        } else {
          let searchVisible = true;
          for (const part of parts) {
            const parsedPart = parseInt(part, 10);
            if (!Number.isNaN(parsedPart) && !ignoreCutPositionCount) {
              const matchNumeric =
                parsedPart === enzyme.get('cutPositions').length ||
                names.some((name) => name.indexOf(part) >= 0);
              searchVisible = searchVisible && matchNumeric;
            } else {
              searchVisible = searchVisible && names.some((name) => name.indexOf(part) >= 0);
            }
          }
          enzyme.set('catalogVisible', searchVisible);
        }
      } else {
        enzyme.set('catalogVisible', false);
      }
    } else {
      enzyme.set('catalogVisible', false);
    }
  }

  restoreState(): void {
    this.$el.find('.search').val(this.panelState.get('search'));
    this.$el.find('.view-enzymes').val(this.panelState.get('viewEnzymes'));
    this.$el.find('.enzymes_list').val(JSON.stringify(this.panelState.get('enzymeListId')));
    this.$el.find('.panel').removeClass('show-sidebar');
  }

  showEnzymeLists(): void {
    const $select = this.$el.find('.enzyme-lists');
    $select.empty();

    const $commercialGroup = $('<optgroup>').attr('label', 'Commercial');
    for (const {id, name} of EnzymeConstants.providerLists) {
      $commercialGroup.append(new Option(name, JSON.stringify(id)));
    }

    const $customGroup = $('<optgroup>').attr('label', 'Custom Lists');
    // @ts-expect-error window.bootstrap is possibly undefined
    for (const list of window.bootstrap.enzyme_lists) {
      let text;
      if (list.owner_handle === window.user.handle) {
        text = list.name;
      } else {
        text = `${list.owner_handle} / ${list.name}`;
      }
      $customGroup.append(new Option(text, JSON.stringify(list.id)));
    }

    $select.append($commercialGroup);
    $select.append($customGroup);

    const id = this.panelState.get('enzymeListId');
    if (EnzymeUtil.isEnzymeListId(id)) {
      $select.val(JSON.stringify(id));
    } else {
      this.panelState.set('enzymeListId', EnzymeConstants.DEFAULT_ENZYMES_LIST_ID);
      $select.val(JSON.stringify(EnzymeConstants.DEFAULT_ENZYMES_LIST_ID));
    }
  }

  updateHighlightCompatible(state: RestrictionPanelState, highlight: boolean): void {
    this.$el.find('.checkbox input').prop('checked', highlight);
  }

  changeCompatible(): void {
    this.panelState.set({highlightCompatible: this.$el.find('.checkbox input').prop('checked')});
  }

  updateCutRegion(): void {
    const reallyBig = this.sequence.isReallyBig();
    if (reallyBig) {
      this.$('.cut-region select').prop('disabled', true).val('anywhere');
    } else {
      this.$('.cut-region select').prop('disabled', false);
    }
  }

  update(panelState: RestrictionPanelState, sequence: Sequence): void {
    if (showValueMessagingForSharedFile()) {
      this.$el.find('.run-digest').addClass('disabled');
      renderToDOM(
        <ToolbarSignupMessage action="run a digest" />,
        this.$el.find('.share-link-message-react-mount')[0]
      );
    }

    // panelState listeners
    if (this.panelState != null) {
      this.stopListening(this.panelState);
    }
    this.panelState = panelState;
    this.buffers = panelState.get('buffers');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.panelState, 'change:activeSubPanel', this.showSubPanel);
    this.listenTo(this.panelState, 'change:enzymeListId', (state: RestrictionPanelState, id: number) => {
      analytics.track('digest', 'changed_enzyme_list', {custom: id !== -1, id});
    });
    this.listenTo(
      this.panelState,
      'change:viewEnzymes',
      (state: RestrictionPanelState, viewEnzymes: boolean) => {
        analytics.track('digest', 'changed_enzyme_visibility', {visible: viewEnzymes});
      }
    );
    this.listenTo(
      this.panelState,
      'change:highlightCompatible',
      // eslint-disable-next-line no-restricted-syntax
      _.bind(
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.updateHighlightCompatible,
        this
      )
    );
    this.updateHighlightCompatible(this.panelState, this.panelState.get('highlightCompatible'));
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.panelState, 'change:cutRegion', this.filter);
    this.listenTo(this.panelState.get('selectedEnzymes'), 'add remove reset', () => {
      this.renderActiveEnzymes();
    });
    this.listenTo(this.panelState, 'change:enzymesProvider', () => {
      this.renderSidebar();
    });

    // enzymes listeners
    if (this.enzymes != null) {
      this.stopListening(this.enzymes);
    }
    this.enzymes = sequence.get('enzymes');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.enzymes, 'showDetail', this.renderSidebar);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.enzymes, 'change:cutPositions', this.filterEnzyme);

    // We're using a batch listener here on all enzymes changes because each individual enzyme stores its own
    // state about whether it should be visible in the catalog and each individual enzyme has this state
    // updated separately. We don't want to rerender the entire enzyme catalog each time one enzyme has its
    // catalogVisible state changed.
    const batchEnzymeListener = this.listenToBatch(this.enzymes, () => {
      this.renderEnzymeCatalog();
    });
    // This listener also handles changes to enzymes' active and highlight status.
    this.listenTo(this.enzymes, 'change', () => {
      batchEnzymeListener();
    });

    this.listenTo(this.enzymes, 'zoomTo', ({offset, enzyme}: {offset: number; enzyme: Enzyme}) => {
      // FIXME: When this is fully React then we can do this in a normal way but for now we suffer.
      // Ideally, we would pass a callback to any component that would trigger this event that just calls
      // scrollIntoView on a ref to the corresponding enzyme row.
      // Find the location of the el of the catalog enzyme. It may have become hidden since its addition to
      // active enzymes.
      const index = this.enzymes.filter((enzyme) => enzyme.get('catalogVisible')).indexOf(enzyme);
      if (index === -1) {
        return;
      }
      // If the catalog enzyme el exists, scroll the catalog to the top of that el.
      const enzymeEl = this.$el.find('table.catalogue tbody').children()[index];
      this.$el
        .find('table.catalogue tbody')
        .stop()
        .animate({scrollTop: enzymeEl.offsetTop - offset}, 200);
    });

    // sequence listeners
    if (this.sequence != null) {
      this.stopListening(this.sequence._sequence());
      this.stopListening(this.sequence.get('analyses'));
    }
    this.sequence = sequence;
    this.listenTo(this.sequence._sequence(), 'change:bases change:startIndex', () => {
      this.renderSidebar();
    });
    this.listenTo(this.sequence.get('analyses'), 'add remove change', () => {
      this.renderLibrary();
    });

    // selection listeners
    if (this.selection != null) {
      this.stopListening(this.selection);
    }
    this.selection = sequence.get('selection');
    this.listenTo(
      this.selection,
      'change:start change:end',
      // eslint-disable-next-line no-restricted-syntax
      _.bind(function () {
        // @ts-expect-error 'this' shadowing
        this.updateCutRegion();
        // @ts-expect-error 'this' shadowing
        this.filter();
      }, this)
    );
    this.updateCutRegion();

    this.restoreState();
    this.render();
  }

  sequenceSizeSetup(): void {
    // We do not know if an enzyme is a double or single cutter,
    // since we aren't computing all cutsites.
    // It is also very unlikely for there to be any single or double cutters.

    const reallyBig = this.sequence.isReallyBig();

    // Disable single-double cutter ptions.
    const selectors = [
      '.view-enzymes option[value="single"]',
      '.view-enzymes option[value="double"]',
      '.view-enzymes option[value="singleanddouble"]',
    ];
    const selector = selectors.join(',');
    const singleDoubleOptions = this.$(selector);
    singleDoubleOptions.prop('disabled', reallyBig);
    const singleDoubleSelected = singleDoubleOptions.filter(':selected').length > 0;

    // Default to really
    if (singleDoubleSelected && reallyBig) {
      this.$('.view-enzymes').val('none');
    }

    // Hide cutsite positions
    if (reallyBig) {
      this.$('#enzymes-header .cut-header').hide();
    } else {
      this.$('#enzymes-header .cut-header').show();
    }

    // Don't filter by number of cutsites
    if (reallyBig) {
      this.$('.search').attr('placeholder', 'Search by name');
    } else {
      this.$('.search').attr('placeholder', 'Search by name or number of cuts');
    }

    // Message
    if (reallyBig) {
      this.$('.filter-message').text('Filtering by cutsites is temporarily disabled on large sequences.');
    } else {
      this.$('.filter-message').text('');
    }
  }

  toggleNucleotideTypeDependentElements(): void {
    const isRNASequence = this.panelState.get('sequence').isRNASequence();
    this.$('.dna-none').toggle(!isRNASequence);
    this.$('.rna-none').toggle(isRNASequence);
    this.$('.panel-title').toggle(!isRNASequence);
    this.$('.find-enzyme').toggle(!isRNASequence);
  }

  render(): this {
    this.sequenceSizeSetup();
    this.showEnzymeLists();
    this.filter();
    this.toggleNucleotideTypeDependentElements();
    if (!this.panelState.get('sequence').isRNASequence()) {
      this.showSubPanel();
      this.renderEnzymes();
      this.renderLibrary();
    }
    return this;
  }
}
RestrictionPanelView.initClass();
